package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst

/**
 * AST 表达式 / 语句的访问者基类：提供"默认全递归"的 walk 模板。
 *
 * 设计意图：semantic 包下多个 Checker（CloseBlockChecker / AwaitPositionChecker /
 * SubscribeChecker / DataLiteralChecker / DefaultValueChecker / ReferenceResolver
 * 等）都自带一份 30+ 分支的 ExprAst 子节点递归代码——逻辑高度重复但每个 Checker
 * 又有少量定制（特殊节点处理 / Lambda 体进入策略 / 错误码差异）。本类把"默认全
 * 递归子节点"的样板一次性收口，子类只需 override 对自己关心的节点的 visitXxx。
 *
 * 类型参数 [C]：由具体 Checker 自行选择的上下文类型（例如累积错误的 Set、栈帧、
 * 或 Unit 表示无上下文）。所有 visit 方法都接收同一份 ctx 引用，便于在递归过程中
 * 维护可变状态。
 *
 * 使用约定：
 * - 入口：[visit] / [visitStmt] / [visitLValue]
 * - 子类 override visitXxx 时，若希望保持子节点继续被遍历，必须显式调
 *   `super.visitXxx(e, ctx)`。这与 Kotlin 默认实现风格一致——明确"我要在做完
 *   自己的事之后继续递归"还是"我要打断递归"。
 * - Lambda 体进入策略：默认 [visitLambda] 进入 body；CloseBlockChecker 风格的
 *   "lambda 内属于另一执行上下文，由其它 checker 把关"可通过子类 override 为
 *   `Unit` 关闭进入。
 *
 * 不在本类范围：
 * - 不替换现有 9 个 Checker 的 walker（属于历史债，单独 RFC 渐进收编更安全）。
 * - 不引入元素树 / 屏幕级遍历——那些与 Checker 业务耦合，应在各 Checker 自己
 *   的入口方法里组织。
 */
abstract class ExprVisitor<C> {

    /** 表达式派发入口：根据具体类型路由到 visitXxx。 */
    open fun visit(e: ExprAst, ctx: C) {
        when (e) {
            // 叶子节点
            is ExprAst.Literal -> visitLiteral(e, ctx)
            is ExprAst.NullLit -> visitNullLit(e, ctx)
            is ExprAst.IdentRef -> visitIdentRef(e, ctx)
            is ExprAst.LocalRef -> visitLocalRef(e, ctx)
            is ExprAst.StateRef -> visitStateRef(e, ctx)
            is ExprAst.SessionRef -> visitSessionRef(e, ctx)
            is ExprAst.PersistRef -> visitPersistRef(e, ctx)
            is ExprAst.DerivedRef -> visitDerivedRef(e, ctx)
            is ExprAst.PropsRef -> visitPropsRef(e, ctx)
            is ExprAst.ThemeRef -> visitThemeRef(e, ctx)
            is ExprAst.FunRefExpr -> visitFunRef(e, ctx)
            // 复合节点
            is ExprAst.Interp -> visitInterp(e, ctx)
            is ExprAst.BinaryOp -> visitBinaryOp(e, ctx)
            is ExprAst.UnaryOp -> visitUnaryOp(e, ctx)
            is ExprAst.MemberAccess -> visitMemberAccess(e, ctx)
            is ExprAst.SafeMemberAccess -> visitSafeMemberAccess(e, ctx)
            is ExprAst.NonNullAssert -> visitNonNullAssert(e, ctx)
            is ExprAst.MemberCall -> visitMemberCall(e, ctx)
            is ExprAst.SafeMemberCall -> visitSafeMemberCall(e, ctx)
            is ExprAst.Elvis -> visitElvis(e, ctx)
            is ExprAst.Ternary -> visitTernary(e, ctx)
            is ExprAst.IfExpr -> visitIfExpr(e, ctx)
            is ExprAst.WhenExpr -> visitWhenExpr(e, ctx)
            is ExprAst.IsExpr -> visitIsExpr(e, ctx)
            is ExprAst.IndexExpr -> visitIndexExpr(e, ctx)
            is ExprAst.RangeExpr -> visitRangeExpr(e, ctx)
            is ExprAst.ListLiteralExpr -> visitListLiteral(e, ctx)
            is ExprAst.MapLiteralExpr -> visitMapLiteral(e, ctx)
            is ExprAst.LambdaExpr -> visitLambda(e, ctx)
            is ExprAst.CallExpr -> visitCallExpr(e, ctx)
            is ExprAst.BuiltinCallExpr -> visitBuiltinCall(e, ctx)
            is ExprAst.AwaitExpr -> visitAwait(e, ctx)
            is ExprAst.NetRequestCall -> visitNetRequest(e, ctx)
            is ExprAst.NetSubscribeCall -> visitNetSubscribe(e, ctx)
        }
    }

    // ===== 叶子节点：默认 no-op，无子节点可递归 =====
    open fun visitLiteral(e: ExprAst.Literal, ctx: C) = Unit
    open fun visitNullLit(e: ExprAst.NullLit, ctx: C) = Unit
    open fun visitIdentRef(e: ExprAst.IdentRef, ctx: C) = Unit
    open fun visitLocalRef(e: ExprAst.LocalRef, ctx: C) = Unit
    open fun visitStateRef(e: ExprAst.StateRef, ctx: C) = Unit
    open fun visitSessionRef(e: ExprAst.SessionRef, ctx: C) = Unit
    open fun visitPersistRef(e: ExprAst.PersistRef, ctx: C) = Unit
    open fun visitDerivedRef(e: ExprAst.DerivedRef, ctx: C) = Unit
    open fun visitPropsRef(e: ExprAst.PropsRef, ctx: C) = Unit
    open fun visitThemeRef(e: ExprAst.ThemeRef, ctx: C) = Unit
    open fun visitFunRef(e: ExprAst.FunRefExpr, ctx: C) = Unit

    // ===== 复合节点：默认全递归 =====

    open fun visitInterp(e: ExprAst.Interp, ctx: C) {
        for (part in e.parts) {
            if (part is ExprAst.Interp.InterpPart.ExprPart) visit(part.expr, ctx)
        }
    }

    open fun visitBinaryOp(e: ExprAst.BinaryOp, ctx: C) {
        visit(e.left, ctx); visit(e.right, ctx)
    }

    open fun visitUnaryOp(e: ExprAst.UnaryOp, ctx: C) {
        visit(e.operand, ctx)
    }

    open fun visitMemberAccess(e: ExprAst.MemberAccess, ctx: C) {
        visit(e.target, ctx)
    }

    open fun visitSafeMemberAccess(e: ExprAst.SafeMemberAccess, ctx: C) {
        visit(e.target, ctx)
    }

    /** M10 Stage 6：`target!!` 默认透传 target 子表达式。 */
    open fun visitNonNullAssert(e: ExprAst.NonNullAssert, ctx: C) {
        visit(e.target, ctx)
    }

    open fun visitMemberCall(e: ExprAst.MemberCall, ctx: C) {
        visit(e.target, ctx)
        e.args.forEach { visit(it, ctx) }
        e.namedArgs.forEach { visit(it.value, ctx) }
    }

    open fun visitSafeMemberCall(e: ExprAst.SafeMemberCall, ctx: C) {
        visit(e.target, ctx)
        e.args.forEach { visit(it, ctx) }
    }

    open fun visitElvis(e: ExprAst.Elvis, ctx: C) {
        visit(e.left, ctx); visit(e.right, ctx)
    }

    open fun visitTernary(e: ExprAst.Ternary, ctx: C) {
        visit(e.cond, ctx)
        visit(e.thenExpr, ctx)
        visit(e.elseExpr, ctx)
    }

    open fun visitIfExpr(e: ExprAst.IfExpr, ctx: C) {
        visit(e.cond, ctx)
        e.thenBody.forEach { visitStmt(it, ctx) }
        e.elseBody.forEach { visitStmt(it, ctx) }
    }

    open fun visitWhenExpr(e: ExprAst.WhenExpr, ctx: C) {
        visit(e.subject, ctx)
        e.branches.forEach { br ->
            br.values.forEach { visit(it, ctx) }
            br.body.forEach { visitStmt(it, ctx) }
        }
        e.elseBody?.forEach { visitStmt(it, ctx) }
    }

    open fun visitIsExpr(e: ExprAst.IsExpr, ctx: C) {
        visit(e.target, ctx)
    }

    open fun visitIndexExpr(e: ExprAst.IndexExpr, ctx: C) {
        visit(e.target, ctx); visit(e.index, ctx)
    }

    open fun visitRangeExpr(e: ExprAst.RangeExpr, ctx: C) {
        visit(e.start, ctx); visit(e.endValue, ctx)
    }

    open fun visitListLiteral(e: ExprAst.ListLiteralExpr, ctx: C) {
        e.elements.forEach { visit(it, ctx) }
    }

    open fun visitMapLiteral(e: ExprAst.MapLiteralExpr, ctx: C) {
        e.entries.forEach { visit(it.value, ctx) }
    }

    /**
     * 默认进入 lambda body；子类如需"lambda 内属于另一执行上下文，由其它 checker
     * 把关"语义（CloseBlockChecker 风格）可 override 为 `Unit`。
     */
    open fun visitLambda(e: ExprAst.LambdaExpr, ctx: C) {
        e.body.forEach { visitStmt(it, ctx) }
    }

    open fun visitCallExpr(e: ExprAst.CallExpr, ctx: C) {
        visit(e.callee, ctx)
        e.args.forEach { visit(it, ctx) }
        e.namedArgs.forEach { visit(it.value, ctx) }
    }

    open fun visitBuiltinCall(e: ExprAst.BuiltinCallExpr, ctx: C) {
        e.args.forEach { visit(it, ctx) }
        e.namedArgs.forEach { visit(it.value, ctx) }
    }

    open fun visitAwait(e: ExprAst.AwaitExpr, ctx: C) {
        e.tailExpr?.let { visit(it, ctx) }
    }

    open fun visitNetRequest(e: ExprAst.NetRequestCall, ctx: C) {
        e.payload.forEach { visit(it.value, ctx) }
        e.callback?.let { visit(it, ctx) }
        e.timeoutMs?.let { visit(it, ctx) }
    }

    /** Stage B：默认递归 handler 子节点。 */
    open fun visitNetSubscribe(e: ExprAst.NetSubscribeCall, ctx: C) {
        visit(e.handler, ctx)
    }

    // ===== 语句派发与默认全递归 =====

    /** 语句派发入口：根据具体类型路由到默认全递归实现。 */
    open fun visitStmt(s: StmtAst, ctx: C) {
        when (s) {
            is StmtAst.AssignStmt -> {
                visitLValue(s.lhs, ctx)
                visit(s.rhs, ctx)
            }
            is StmtAst.VarDeclStmt -> visit(s.initializer, ctx)
            is StmtAst.ExprStmt -> visit(s.call, ctx)
            is StmtAst.IfStmt -> {
                visit(s.cond, ctx)
                s.thenBody.forEach { visitStmt(it, ctx) }
                s.elseBody.forEach { visitStmt(it, ctx) }
            }
            is StmtAst.WhileStmt -> {
                visit(s.cond, ctx)
                s.body.forEach { visitStmt(it, ctx) }
            }
            is StmtAst.ForInStmt -> {
                visit(s.iterable, ctx)
                s.body.forEach { visitStmt(it, ctx) }
            }
            is StmtAst.WhenStmt -> visit(s.whenExpr, ctx)
            is StmtAst.ReturnStmt -> s.value?.let { visit(it, ctx) }
            is StmtAst.TryStmt -> {
                s.tryBody.forEach { visitStmt(it, ctx) }
                s.catchBody.forEach { visitStmt(it, ctx) }
                s.finallyBody.forEach { visitStmt(it, ctx) }
            }
            // batch 语句：递归 visit body 内每条语句（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> s.body.forEach { visitStmt(it, ctx) }
            is StmtAst.FunDeclStmt -> s.decl.body.forEach { visitStmt(it, ctx) }
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    /** 左值派发入口：默认仅对 IndexLValue 递归子节点，State/Session/Persist/Local 是叶子。 */
    open fun visitLValue(lhs: LValueAst, ctx: C) {
        when (lhs) {
            is LValueAst.IndexLValue -> {
                visit(lhs.target, ctx)
                visit(lhs.index, ctx)
            }
            is LValueAst.StateLValue, is LValueAst.SessionLValue,
            is LValueAst.PersistLValue, is LValueAst.LocalLValue -> Unit
        }
    }
}
